import { Check, CircleAlert } from "lucide-react"

import { formatDateTimeInTimeZone } from "../../lib/format-date"
import {
  formatRunDuration,
  orderScheduledJobRuns,
  scheduledRunDetailLines,
  runResultLabel,
  runStatusLabel,
  scheduleRelative,
  scheduleDuration,
  runTriggerLabel,
} from "../../lib/scheduled-job-display"
import { cn } from "../../lib/utils"
import type { ScheduledJobRun } from "../../services/use-scheduled-jobs"
import { splitActor } from "../runs/actor-label"
import { Table, type TableColumn } from "../ui/table"

type Props = {
  runs: readonly ScheduledJobRun[]
  timeZone: string
  isLoading: boolean
  error: unknown
}
export function ScheduleRunHistory({ runs, timeZone, isLoading, error }: Props) {
  if (isLoading) {
    return (
      <div className="card schedule-loading" aria-label="Loading run history">
        <div className="h-24 animate-pulse bg-muted" />
      </div>
    )
  }
  if (error) {
    return (
      <p role="alert" className="card empty">
        Run history could not be loaded: {error instanceof Error ? error.message : "Unknown error"}
      </p>
    )
  }
  if (!runs.length) {
    return <p className="card empty">No runs yet. Use Run now to start the first one.</p>
  }
  return (
    <section aria-label="Run history">
      <DurationChart runs={runs} timeZone={timeZone} />

      <div className="card runs-card">
        <Table
          columns={columns(timeZone)}
          rows={orderScheduledJobRuns(runs)}
          rowKey={(run) => run.id}
          embedded
          className="table-shell"
          tableClassName="runs"
          rowClassName={() => "enter"}
        />
      </div>
    </section>
  )
}
function duration(run: ScheduledJobRun) {
  if (!run.startedAt || !run.finishedAt) {
    return null
  }
  const value = Date.parse(run.finishedAt) - Date.parse(run.startedAt)
  return Number.isFinite(value) && value >= 0 ? value : null
}
function DurationChart({ runs, timeZone }: Pick<Props, "runs" | "timeZone">) {
  const recent = runs
    .slice(0, 25)
    .toSorted((left, right) => Date.parse(left.triggeredAt) - Date.parse(right.triggeredAt))
  const longest = Math.max(...recent.map((run) => duration(run) ?? 0), 1)
  const first = recent[0]
  const last = recent.at(-1)
  return (
    <div className="card chart-card">
      <div className="chart-head">
        <h2>Duration of last {recent.length} runs</h2>

        <div className="legend">
          <span>
            <i style={{ background: "var(--bar-ok)" }} />
            Succeeded
          </span>

          <span>
            <i style={{ background: "var(--color-destructive)" }} />
            Failed
          </span>

          {recent.some((run) => run.effectiveStatus === "running") ? (
            <span>
              <i style={{ background: "var(--color-info)" }} />
              Running
            </span>
          ) : null}
        </div>
      </div>

      <div
        className="chart"
        role="img"
        aria-label={`Bar chart of run durations; tallest ${scheduleDuration(longest)}`}
      >
        {recent.map((run) => (
          <span
            key={run.id}
            className={cn("bar", {
              fail: run.effectiveStatus === "failed" || run.effectiveStatus === "interrupted",
              run: run.effectiveStatus === "running",
            })}
            style={{
              height: `${run.effectiveStatus === "running" ? 30 : Math.max(6, ((duration(run) ?? 0) / longest) * 100)}%`,
            }}
            title={`${formatDateTimeInTimeZone(run.triggeredAt, timeZone)} · ${runStatusLabel(run)} · ${scheduleDuration(duration(run))}`}
          />
        ))}
      </div>

      <div className="chart-axis">
        <span>{first ? formatDateTimeInTimeZone(first.triggeredAt, timeZone) : ""}</span>

        <span>Longest {scheduleDuration(longest)}</span>

        <span>{last ? formatDateTimeInTimeZone(last.triggeredAt, timeZone) : ""}</span>
      </div>
    </div>
  )
}
function RunStatus({ run }: { run: ScheduledJobRun }) {
  if (run.effectiveStatus === "succeeded") {
    return (
      <span className="status muted">
        <Check className="icon sm c-success" />
        Succeeded
      </span>
    )
  }
  if (run.effectiveStatus === "failed" || run.effectiveStatus === "interrupted") {
    return (
      <span className="badge destructive">
        <CircleAlert className="icon sm" />

        {runStatusLabel(run)}
      </span>
    )
  }
  return (
    <span className={run.effectiveStatus === "coalesced" ? "badge muted" : "badge info"}>
      <span className={run.effectiveStatus === "running" ? "dot pulse" : "dot"} />

      {runStatusLabel(run)}
    </span>
  )
}
function RunDetails({ run }: { run: ScheduledJobRun }) {
  const details = scheduledRunDetailLines(run)
  const result = runResultLabel(run)
  const hasResult = !["Completed", "In progress", "Waiting", run.failureSummary].includes(result)
  return (
    <>
      <div className="run-details">
        <RunStatus run={run} />
      </div>

      {run.failureSummary ? <div className="fail-msg">{run.failureSummary}</div> : null}

      {hasResult ? <div className="sub">{result}</div> : null}

      {details.length ? (
        <ul className="sub">
          {details.map((detail) => (
            <li key={detail}>{detail}</li>
          ))}
        </ul>
      ) : null}
    </>
  )
}
function columns(timeZone: string): TableColumn<ScheduledJobRun>[] {
  return [
    {
      key: "started",
      header: "Started",
      render: (run) => (
        <>
          <span className="tnum od-nowrap">
            {formatDateTimeInTimeZone(run.startedAt ?? run.triggeredAt, timeZone)}
          </span>

          <div className="sub">{scheduleRelative(run.startedAt ?? run.triggeredAt, timeZone)}</div>
        </>
      ),
    },
    { key: "status", header: "Status", render: (run) => <RunDetails run={run} /> },
    {
      key: "duration",
      header: "Duration",
      className: "tnum",
      render: (run) => formatRunDuration(run) ?? "—",
    },
    {
      key: "trigger",
      header: "Trigger",
      render: (run) =>
        run.trigger === "scheduled" ? (
          <span className="muted">Schedule</span>
        ) : (
          <>
            <div>{run.trigger.startsWith("manual_") ? "Manual" : runTriggerLabel(run.trigger)}</div>

            <div className="sub actor" title={run.triggeredBy ?? "Unknown"}>
              {splitActor(run.triggeredBy ?? "Unknown").handle}
            </div>
          </>
        ),
    },
  ]
}
